import { CreateAddressDto } from "api/actor";
import { CreateRealEstateWithQuestionsRequest, PropertyTypeEnum, QuestionAnswerDto, UpdateRealEstateWithQuestionsRequest } from "api/real-estate";
import { PossibleAnswer } from "features/subscription-process/profiling-process/types/interfaces";
import { RealEstateFormValues } from "../types/realEstateFormValues";

function mapSurveyAnswers(questionId: string, value: PossibleAnswer): QuestionAnswerDto[] {
  if (Array.isArray(value)) {
    return value.map((item) => mapSingleAnswer(questionId, item));
  }
  return [mapSingleAnswer(questionId, value)];
}

function mapSingleAnswer(questionId: string, value: PossibleAnswer | string): QuestionAnswerDto {
  if (typeof value === 'boolean') {
    return {
      questionId,
      answerText: value.toString()
    };
  }

  if (typeof value === 'object' && value !== null && 'id' in value) {
    return {
      questionId,
      possibleAnswerId: value.id
    };
  }

  return {
    questionId,
    answerText: String(value ?? "")
  };
}

// Fonction de mapping vers le DTO
export function mapRealEstateFormValuesToCreateDto(
  riskId: string,
  formData: RealEstateFormValues,
  surveyId: string, personId: string,
  propertyType: PropertyTypeEnum,
  initialMonthlyRent?: number,
  initialCharges?: number,
): CreateRealEstateWithQuestionsRequest {
  // Construction de l'adresse
  const address: CreateAddressDto = {
    line1: formData.address,
    line2: formData.additionalAddress || "",
    line3: null,
    zipCode: formData.postalCode,
    city: formData.city,
    country: formData.country,
    formattedAddress: `${formData.address}, ${formData.city} ${formData.postalCode}, ${formData.country}`,
    googlePlaceId: formData.googlePlaceId
  };

  // Construction dynamique des réponses du questionnaire
  let answers: Array<QuestionAnswerDto> = [];
  if (formData.surveyForm) {
    answers = Object.entries(formData.surveyForm).flatMap(([questionId, value]) =>
      mapSurveyAnswers(questionId, value[0])
    );
  }

  // Construction du DTO final
  const dto: CreateRealEstateWithQuestionsRequest = {
    createRealEstateWithQuestionsCommandDto: {
      createRealEstateCommand: {
        riskId,
        personId,
        address: address,
        name: formData.name,
        area: formData.area,
        roomCount: formData.roomCount,
        monthlyRent: formData.monthlyRent || undefined,
        charges: formData.charges || undefined,
        photo: formData.photo,
        propertyType
      },
      saveAnswersCommand: {
        answers: answers,
        surveyId: surveyId,
        autoEvaluate: true
      }
    }
  };

  return dto;
}

// Fonction de mapping vers le DTO
export function mapRealEstateFormValuesToUpdateDto(riskId: string, formData: RealEstateFormValues, surveyId: string, personId: string, realEstateId: string, propertyType: PropertyTypeEnum): UpdateRealEstateWithQuestionsRequest {
  // Construction de l'adresse
  const address: CreateAddressDto = {
    line1: formData.address,
    line2: formData.additionalAddress || "",
    line3: null,
    zipCode: formData.postalCode,
    city: formData.city,
    country: formData.country,
    formattedAddress: `${formData.address}, ${formData.city} ${formData.postalCode}, ${formData.country}`,
    googlePlaceId: formData.googlePlaceId
  };

  // Construction dynamique des réponses du questionnaire
  let answers: Array<QuestionAnswerDto> = [];
  if (formData.surveyForm) {
    answers = Object.entries(formData.surveyForm).flatMap(([questionId, value]) =>
      mapSurveyAnswers(questionId, value[0])
    );
  }

  // Construction du DTO final
  const dto: UpdateRealEstateWithQuestionsRequest = {
    bienId: realEstateId,
    updateRealEstateWithQuestionsCommandDto: {
      updateRealEstateCommand: {
        riskId,
        personId,
        address: address,
        name: formData.name,
        area: formData.area,
        roomCount: formData.roomCount,
        monthlyRent: formData.monthlyRent || undefined,
        charges: formData.charges || undefined,
        photo: undefined,
        propertyType,
        photoHasChanged: false
      },
      saveAnswersCommand: {
        answers: answers,
        surveyId: surveyId,
        autoEvaluate: true
      }
    }
  };

  return dto;
}
